import React, { useEffect, useRef, useState } from 'react';
import {
    prepareWalletTransfer, submitWalletTransfer, checkWalletTransfer,
    formatTransferAmount, transferExplorerUrl,
} from '../utils/walletTransfers';

export default function WalletTokenTransfer({ token, wallet, connection, busy, runTransferAction, walletIsCurrent, onConfirmed }) {
    const [recipient, setRecipient] = useState('');
    const [amount, setAmount] = useState('');
    const [prepared, setPrepared] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [stage, setStage] = useState('');
    const activeRef = useRef(true);
    const actionRef = useRef(false);
    useEffect(() => {
        activeRef.current = true;
        return () => { activeRef.current = false; };
    }, []);
    const sender = wallet.publicKey?.toBase58?.() || '';
    const isCurrent = () => activeRef.current && walletIsCurrent(sender, connection);
    const pending = result?.status === 'pending';
    const locked = busy || !!stage || pending;
    const explorerUrl = result ? transferExplorerUrl(result.signature, result.network) : '';

    const applyResult = async (next) => {
        setResult(next);
        if (next.status === 'confirmed') {
            setAmount('');
            setPrepared(null);
            try {
                await onConfirmed();
            } catch {
                if (isCurrent()) setError('Transfer confirmed, but balances could not be refreshed. Use Refresh to try again.');
            }
        }
    };

    const review = async (event) => {
        event.preventDefault();
        if (locked || actionRef.current) return;
        actionRef.current = true;
        setError('');
        setResult(null);
        setStage('Preparing transfer...');
        try {
            const next = await runTransferAction(() => prepareWalletTransfer({
                wallet, connection, token, recipientAddress: recipient, amount, isCurrent,
            }));
            if (isCurrent()) setPrepared(next);
        } catch (err) {
            if (isCurrent()) setError(err?.message || 'Unable to prepare this transfer.');
        } finally {
            actionRef.current = false;
            if (isCurrent()) setStage('');
        }
    };

    const send = async () => {
        if (locked || actionRef.current || !prepared) return;
        actionRef.current = true;
        setError('');
        setStage('Confirm in your wallet...');
        try {
            const next = await runTransferAction(() => submitWalletTransfer({
                wallet, connection, prepared, isCurrent,
                onSubmitted: (submitted) => {
                    if (!isCurrent()) return;
                    setResult(submitted);
                    setStage('Waiting for confirmation...');
                },
            }));
            if (isCurrent()) await applyResult(next);
        } catch (err) {
            if (isCurrent()) {
                setError(err?.message || 'Unable to send this transfer.');
                setPrepared(null);
            }
        } finally {
            actionRef.current = false;
            if (isCurrent()) setStage('');
        }
    };

    const checkStatus = async () => {
        if (busy || stage || actionRef.current || !pending) return;
        actionRef.current = true;
        setStage('Checking transaction...');
        setError('');
        try {
            const next = await runTransferAction(() => checkWalletTransfer(connection, result));
            if (isCurrent()) await applyResult(next);
        } catch {
            if (isCurrent()) setError('Unable to check confirmation yet. Check the transaction before sending again.');
        } finally {
            actionRef.current = false;
            if (isCurrent()) setStage('');
        }
    };

    return (
        <details className="wallet-token-transfer">
            <summary className="wallet-detail-summary">Send {token.symbol}</summary>
            <form className="wallet-send-form" aria-label={`Send ${token.symbol}`} onSubmit={review}>
                <div className="wallet-send-fields">
                    <label>
                        Recipient wallet address
                        <input
                            className="muulti-input"
                            value={recipient}
                            onChange={(e) => { setRecipient(e.target.value); setPrepared(null); }}
                            autoComplete="off"
                            autoCapitalize="none"
                            spellCheck={false}
                            required
                            disabled={locked}
                        />
                    </label>
                    <label>
                        Amount ({token.symbol})
                        <input
                            className="muulti-input"
                            value={amount}
                            onChange={(e) => { setAmount(e.target.value); setPrepared(null); }}
                            inputMode="decimal"
                            autoComplete="off"
                            required
                            disabled={locked}
                        />
                    </label>
                </div>
                {prepared && !result && (
                    <div className="wallet-transfer-review" aria-label="Transfer review">
                        <dl>
                            <div><dt>Send</dt><dd>{formatTransferAmount(prepared.amountRaw, prepared.decimals)} {token.symbol}</dd></div>
                            <div><dt>To</dt><dd>{prepared.recipientAddress}</dd></div>
                            <div><dt>Network</dt><dd>{prepared.network === 'custom' ? 'Custom network' : prepared.network}</dd></div>
                            <div><dt>Estimated network fee</dt><dd>{formatTransferAmount(prepared.feeLamports, 9)} SOL</dd></div>
                            {prepared.rentLamports > 0 && <div><dt>Recipient token-account creation</dt><dd>{formatTransferAmount(prepared.rentLamports, 9)} SOL</dd></div>}
                            {prepared.tokenFeeRaw > 0 && <>
                                <div><dt>Token transfer fee</dt><dd>{formatTransferAmount(prepared.tokenFeeRaw, prepared.decimals)} {token.symbol}</dd></div>
                                <div><dt>Recipient receives</dt><dd>{formatTransferAmount(prepared.receivedRaw, prepared.decimals)} {token.symbol}</dd></div>
                            </>}
                        </dl>
                        <p className="wallet-detail-muted">Check the recipient and amount before sending.</p>
                    </div>
                )}
                <div className="wallet-send-actions">
                    {prepared && !result ? (
                        <button type="button" className="muulti-button" onClick={send} disabled={locked}>
                            Confirm and send {token.symbol}
                        </button>
                    ) : !pending && (
                        <button type="submit" className="muulti-button" disabled={locked || !recipient.trim() || !amount.trim()}>
                            Review transfer
                        </button>
                    )}
                    {pending && (
                        <button type="button" className="muulti-button" disabled={busy || !!stage} onClick={checkStatus}>
                            Check status
                        </button>
                    )}
                </div>
                <p className="wallet-detail-muted">Network fees are paid in SOL.</p>
                {stage && <p className="wallet-detail-muted" role="status">{stage}</p>}
                {!stage && result && <p className="wallet-detail-muted" role="status">
                    {result.status === 'confirmed' ? 'Transfer confirmed.' :
                        result.status === 'failed' ? 'Transaction failed on-chain. The transfer was not completed.' :
                            result.status === 'expired' ? 'Transaction expired without confirmation. You can review a new transfer.' :
                                'Transaction submitted. Confirmation is pending; check its status before sending again.'}
                </p>}
                {error && <p className="wallet-detail-error" role="alert">{error}</p>}
                {result?.signature && (explorerUrl ? (
                    <a href={explorerUrl} target="_blank" rel="noopener noreferrer">View transaction</a>
                ) : <p className="wallet-detail-address">Transaction: {result.signature}</p>)}
            </form>
        </details>
    );
}
